import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@agms/ui'
import { PageHeader, SurfaceCard } from '../components/ui'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import { usePermissions } from '../hooks/useAuth'
import api from '../lib/api'
import { formatCurrencyWithCentsOrDash } from '../lib/format'

interface ScheduleAVersion {
  id: string
  name: string
  version_number: number
  effective_on: string
  effective_until: string | null
  status: string
  notes: string | null
  auth_buy_per_item: string
  optblue_per_item_fee: string | null
  monthly_cost: string
  assessment_visa_percent: string
  assessment_mastercard_percent: string
  assessment_discover_percent: string
  assessment_amex_percent: string
  pin_debit_cost_percent?: string | null
  pin_debit_cost_per_item?: string | null
}

const emptyForm = {
  name: '',
  effective_on: '',
  effective_until: '',
  status: 'draft',
  notes: '',
  auth_buy_per_item: '0',
  optblue_per_item_fee: '',
  monthly_cost: '0',
  assessment_visa_percent: '0',
  assessment_mastercard_percent: '0',
  assessment_discover_percent: '0',
  assessment_amex_percent: '0',
  pin_debit_cost_percent: '',
  pin_debit_cost_per_item: '',
}

export function SettingsScheduleAPage() {
  const { canManageScheduleA } = usePermissions()
  const canWrite = canManageScheduleA()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState<string | null>(null)

  const versions = useQuery({
    queryKey: ['schedule-a'],
    queryFn: async () => (await api.get<{ data: ScheduleAVersion[] }>('/schedule-a')).data.data,
  })

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        effective_until: form.effective_until || null,
        notes: form.notes || null,
        optblue_per_item_fee: form.optblue_per_item_fee || null,
      }
      if (editingId) {
        return (await api.patch<ScheduleAVersion>(`/settings/schedule-a/${editingId}`, body)).data
      }
      return (await api.post<ScheduleAVersion>('/settings/schedule-a', body)).data
    },
    onSuccess: () => {
      setShowForm(false)
      setEditingId(null)
      setForm(emptyForm)
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['schedule-a'] })
    },
    onError: (caught) => setError(apiErrorMessage(caught, 'Schedule A could not be saved.')),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => api.delete(`/settings/schedule-a/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedule-a'] }),
    onError: (caught) => setError(apiErrorMessage(caught, 'Schedule A could not be deleted.')),
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Schedule A"
        subtitle="AGMS buy rates used by statement analysis. Versions stay attached to analyses that already used them."
        action={<Link to="/settings" className="text-sm text-sky-700">Settings</Link>}
      />

      {!canWrite ? (
        <p className="text-sm text-gray-600">Only an admin can create, edit, or delete Schedule A.</p>
      ) : null}

      {error ? <p className="text-sm text-red-700" role="alert">{error}</p> : null}

      {canWrite ? (
        <div>
          <Button type="button" onClick={() => { setEditingId(null); setForm(emptyForm); setShowForm(true) }}>
            Create Schedule A
          </Button>
        </div>
      ) : null}

      {canWrite && showForm ? (
        <SurfaceCard>
          <form
            className="grid gap-4 md:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <Input label="Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
            <Input label="Effective on" type="date" value={form.effective_on} onChange={(event) => setForm({ ...form, effective_on: event.target.value })} required />
            <Input label="Effective until" type="date" value={form.effective_until} onChange={(event) => setForm({ ...form, effective_until: event.target.value })} />
            <label className="text-sm font-medium text-gray-700">
              Status
              <select
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={form.status}
                onChange={(event) => setForm({ ...form, status: event.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="superseded">Superseded</option>
              </select>
            </label>
            <Input label="Auth buy per item" value={form.auth_buy_per_item} onChange={(event) => setForm({ ...form, auth_buy_per_item: event.target.value })} />
            <Input label="OptBlue per-item fee" value={form.optblue_per_item_fee} onChange={(event) => setForm({ ...form, optblue_per_item_fee: event.target.value })} />
            <p className="text-xs text-gray-500 md:col-span-2">Leave OptBlue per-item blank when it is unknown. Statement analysis uses this fee only for OptBlue rates that do not state one.</p>
            <Input label="Monthly cost" value={form.monthly_cost} onChange={(event) => setForm({ ...form, monthly_cost: event.target.value })} />
            <Input label="PIN debit cost percent" value={form.pin_debit_cost_percent} onChange={(event) => setForm({ ...form, pin_debit_cost_percent: event.target.value })} />
            <Input label="PIN debit cost per item" value={form.pin_debit_cost_per_item} onChange={(event) => setForm({ ...form, pin_debit_cost_per_item: event.target.value })} />
            <p className="text-xs text-gray-500 md:col-span-2">A blank PIN debit cost stops a quick analysis that includes PIN debit.</p>
            <Input label="Visa assessment percent" value={form.assessment_visa_percent} onChange={(event) => setForm({ ...form, assessment_visa_percent: event.target.value })} />
            <Input label="Mastercard assessment percent" value={form.assessment_mastercard_percent} onChange={(event) => setForm({ ...form, assessment_mastercard_percent: event.target.value })} />
            <Input label="Discover assessment percent" value={form.assessment_discover_percent} onChange={(event) => setForm({ ...form, assessment_discover_percent: event.target.value })} />
            <Input label="Amex assessment percent" value={form.assessment_amex_percent} onChange={(event) => setForm({ ...form, assessment_amex_percent: event.target.value })} />
            <div className="md:col-span-2">
              <Button type="submit" disabled={save.isPending}>{editingId ? 'Save changes' : 'Save Schedule A'}</Button>
            </div>
          </form>
        </SurfaceCard>
      ) : null}

      <div className="space-y-4">
        {versions.data?.map((version) => (
          <SurfaceCard key={version.id}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">{version.name}</h2>
                <p className="text-xs text-gray-500">Version {version.version_number} · {version.status} · effective {String(version.effective_on).slice(0, 10)}</p>
              </div>
              {canWrite ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditingId(version.id)
                      setShowForm(true)
                      setForm({
                        name: version.name,
                        effective_on: String(version.effective_on).slice(0, 10),
                        effective_until: version.effective_until ? String(version.effective_until).slice(0, 10) : '',
                        status: version.status,
                        notes: version.notes ?? '',
                        auth_buy_per_item: version.auth_buy_per_item,
                        optblue_per_item_fee: version.optblue_per_item_fee ?? '',
                        monthly_cost: version.monthly_cost,
                        assessment_visa_percent: version.assessment_visa_percent,
                        assessment_mastercard_percent: version.assessment_mastercard_percent,
                        assessment_discover_percent: version.assessment_discover_percent,
                        assessment_amex_percent: version.assessment_amex_percent,
                        pin_debit_cost_percent: version.pin_debit_cost_percent ?? '',
                        pin_debit_cost_per_item: version.pin_debit_cost_per_item ?? '',
                      })
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      if (window.confirm('Delete this Schedule A version?')) {
                        remove.mutate(version.id)
                      }
                    }}
                  >
                    Delete
                  </Button>
                </div>
              ) : null}
            </div>
            <dl className="mt-4 grid gap-2 text-sm md:grid-cols-2">
              <div><dt className="text-gray-500">Auth buy per item</dt><dd>{version.auth_buy_per_item}</dd></div>
              <div><dt className="text-gray-500">OptBlue per-item fee</dt><dd>{version.optblue_per_item_fee ?? 'unknown'}</dd></div>
              <div><dt className="text-gray-500">Monthly cost</dt><dd>{formatCurrencyWithCentsOrDash(version.monthly_cost)}</dd></div>
              <div><dt className="text-gray-500">Visa assessment percent</dt><dd>{version.assessment_visa_percent}</dd></div>
              <div><dt className="text-gray-500">Mastercard assessment percent</dt><dd>{version.assessment_mastercard_percent}</dd></div>
              <div><dt className="text-gray-500">Discover assessment percent</dt><dd>{version.assessment_discover_percent}</dd></div>
              <div><dt className="text-gray-500">Amex assessment percent</dt><dd>{version.assessment_amex_percent}</dd></div>
            </dl>
          </SurfaceCard>
        ))}
      </div>
    </div>
  )
}
